import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tabs,
  Tab,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  SecurityOutlined as ShieldIcon,
  HistoryOutlined as HistoryIcon,
  Business as BusinessIcon,
  BadgeOutlined as BadgeIcon,
} from '@mui/icons-material';

import PageLayout from '@/components/Layout/PageLayout';
import CardHeader from '@/components/Card/CardHeader';
import UserBasicInfoSection from '../UserManagement/components/UserBasicInfoSection';
import UserPermissionsSection from '../UserManagement/components/UserPermissionsSection';
import UserHistorySection from '../UserManagement/components/UserHistorySection';
import UserEmployeeInfoSection from '../UserManagement/components/UserEmployeeInfoSection';
import { useUserDetails } from './hooks/useUserDetails';

const UserDetails = () => {
  const {
    userId,
    user,
    loading,
    fetchError,
    updateLoading,
    updateError,
    showSuccessAlert,
    successMessage,
    resendLoading,
    resendError,
    activeTab,
    basicInfoRef,
    employeeInfoRef,
    facilityPermissions,
    organizationPermissions,
    permissionsType,
    selectedRoleId,
    accessMode,
    selectedFacilities,
    selectedFacilityGroupIds,
    selectedOrganization,
    facilities,
    organizations,
    permissionsTemplate,
    roleBasedFacilitiesAccess,
    guardError,
    isFormDisabled,
    handleBack,
    handleSuccessClose,
    handleBasicInfoChange,
    handlePermissionChange,
    handlePermissionsTypeChange,
    handleRoleChange,
    handleOrganizationChange,
    handleAccessModeChange,
    handleTabChange,
    handleSave,
    handleResendActivation,
    setSelectedFacilities,
    setSelectedFacilityGroupIds,
    setRoleBasedFacilitiesAccess,
    setEmployeeInfo,
  } = useUserDetails();

  if (loading) {
    return (
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
              <PersonIcon sx={{ color: 'white', mr: 1 }} />
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500 }}
                component='h4'>
                User Details
              </Typography>
            </Box>
          </CardHeader>
        }>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (fetchError) {
    return (
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
              <PersonIcon sx={{ color: 'white', mr: 1 }} />
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500 }}
                component='h4'>
                User Details
              </Typography>
            </Box>
          </CardHeader>
        }>
        <Alert severity='error' sx={{ m: 2 }}>
          {fetchError}
        </Alert>
      </PageLayout>
    );
  }

  if (!user) {
    return (
      <PageLayout
        header={
          <CardHeader color='primary'>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}>
                <ArrowBackIcon sx={{ color: 'white' }} />
              </IconButton>
              <PersonIcon sx={{ color: 'white', mr: 1 }} />
              <Typography
                variant='h6'
                sx={{ color: 'white', fontWeight: 500 }}
                component='h4'>
                User Details
              </Typography>
            </Box>
          </CardHeader>
        }>
        <Alert severity='warning' sx={{ m: 2 }}>
          User not found
        </Alert>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      height='auto'
      padding={{ pt: 2, px: 2, pb: 4 }}
      header={
        <CardHeader color='primary'>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={handleBack}
              sx={{
                mr: 2,
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                },
              }}>
              <ArrowBackIcon sx={{ color: 'white' }} />
            </IconButton>
            <PersonIcon sx={{ color: 'white', mr: 1 }} />
            <Typography
              variant='h6'
              sx={{ color: 'white', fontWeight: 500 }}
              component='h4'>
              User Details: {user.user_data?.name}
            </Typography>
          </Box>
        </CardHeader>
      }>
      {showSuccessAlert && successMessage && (
        <Alert
          id='success-alert'
          severity='success'
          sx={{ mb: 3 }}
          onClose={handleSuccessClose}>
          {successMessage}
        </Alert>
      )}

      {updateError && (
        <Alert id='error-alert' severity='error' sx={{ mb: 3 }}>
          {updateError}
        </Alert>
      )}

      {guardError && (
        <Alert severity='warning' sx={{ mb: 3 }}>
          {guardError}
        </Alert>
      )}

      {resendError && (
        <Alert id='resend-error-alert' severity='error' sx={{ mb: 3 }}>
          {resendError}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant='scrollable'
          scrollButtons='auto'>
          <Tab
            icon={<PersonIcon />}
            iconPosition='start'
            label='User Details'
          />
          <Tab
            icon={<BusinessIcon />}
            iconPosition='start'
            label='Organization Permissions'
          />
          <Tab
            icon={<ShieldIcon />}
            iconPosition='start'
            label='Facility Permissions'
          />
          <Tab
            icon={<BadgeIcon />}
            iconPosition='start'
            label='Employee Info'
          />
          <Tab icon={<HistoryIcon />} iconPosition='start' label='History' />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <UserBasicInfoSection
          ref={basicInfoRef}
          user={user}
          onFormChange={handleBasicInfoChange}
          facilities={facilities}
          organizations={organizations}
          selectedFacilities={selectedFacilities}
          onSelectedFacilitiesChange={setSelectedFacilities}
          onResendActivation={handleResendActivation}
          resendLoading={resendLoading}
          permissionsType={permissionsType}
          onPermissionsTypeChange={handlePermissionsTypeChange}
          selectedRoleId={selectedRoleId}
          onRoleChange={handleRoleChange}
          selectedOrganization={selectedOrganization}
          onOrganizationChange={handleOrganizationChange}
          onAccessModeChange={handleAccessModeChange}
          roleBasedFacilitiesAccess={roleBasedFacilitiesAccess}
          setRoleBasedFacilitiesAccess={setRoleBasedFacilitiesAccess}
          accessMode={accessMode}
          selectedFacilityGroupIds={selectedFacilityGroupIds}
          onSelectedFacilityGroupIdsChange={setSelectedFacilityGroupIds}
        />
      )}

      {activeTab === 1 && (
        <UserPermissionsSection
          permissions={organizationPermissions}
          onPermissionChange={(newPermissions, targetId) =>
            handlePermissionChange(newPermissions, targetId, 'organization')
          }
          permissionsTemplate={permissionsTemplate}
          facilities={facilities}
          organizations={organizations}
          selectedFacilities={selectedFacilities}
          editingUserId={user.user_data?.id}
          permissionsType={permissionsType}
          onPermissionsTypeChange={handlePermissionsTypeChange}
          selectedRoleId={selectedRoleId}
          permissionLevel='organization'
          currentUser={user.user_data}
        />
      )}

      {activeTab === 2 && (
        <UserPermissionsSection
          permissions={facilityPermissions}
          onPermissionChange={(newPermissions, targetId) =>
            handlePermissionChange(newPermissions, targetId, 'facility')
          }
          permissionsTemplate={permissionsTemplate}
          facilities={facilities}
          organizations={organizations}
          selectedFacilities={selectedFacilities}
          editingUserId={user.user_data?.id}
          permissionsType={permissionsType}
          onPermissionsTypeChange={handlePermissionsTypeChange}
          selectedRoleId={selectedRoleId}
          permissionLevel='facility'
          currentUser={user.user_data}
        />
      )}

      {activeTab === 3 && (
        <UserEmployeeInfoSection
          ref={employeeInfoRef}
          user={user}
          onFormChange={setEmployeeInfo}
        />
      )}

      {activeTab === 4 && <UserHistorySection userId={userId} />}

      {activeTab <= 3 &&
        !(activeTab > 0 && activeTab < 3 && permissionsType === 'role') && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 3 }}>
            <Button
              onClick={handleSave}
              variant='contained'
              startIcon={
                updateLoading ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                  <SaveIcon sx={{ color: 'white' }} />
                )
              }
              disabled={isFormDisabled}
              sx={{
                color: 'white',
                '& .MuiButton-startIcon': {
                  color: 'white',
                },
              }}>
              {updateLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        )}
    </PageLayout>
  );
};

export default UserDetails;
